import type { Row } from "@tanstack/react-table"
import { useCallback } from "react"

import usePurchaseOrderForm from "../../hooks/usePurchaseOrderForm"

import type { OrderLine } from "@/graphql/codegen/graphql"
import { OrderStatus } from "@/graphql/codegen/graphql"
import { router } from "@/providers/GraphqlRouterProvider"
import { NotificationType } from "@/schemas/notification"
import { useUpdatePO } from "@/screens/Procurement/PurchaseOrder/DeliveryTermsAndShippingLegs/hooks/useUpdatePO"
import { useBackAction } from "@/screens/Procurement/PurchaseOrder/hooks/useHeaderActions"
import { createOrderUpdateInput } from "@/screens/Procurement/PurchaseOrder/hooks/utils/createOrderUpdateInput"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseActionsColumn {
  onRemoveRow: () => void
  onCancelPO: () => void
  multipleProductsPO: boolean
}

export const useActionsColumn = ({ row }: { row: Row<OrderLine> }): UseActionsColumn => {
  const { formValues: order, setField, getField } = usePurchaseOrderForm()
  const orderLines = getField("orderLines")
  const { handleBack } = useBackAction()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { updatePO } = useUpdatePO({
    callback: () => {
      notify({
        type: NotificationType.Success,
        message: `Purchase Order ${order.orderId} cancelled`,
      })

      if (order.clonedFromOrderId) {
        router.navigate({
          to: "/app/procurement/purchase-orders/$purchaseOrderId",
          params: { purchaseOrderId: order.clonedFromOrderId.toString() },
          replace: true,
        })
      } else {
        router.history.back()
      }
    },
  })

  const onRemoveRow = useCallback(() => {
    if (!orderLines) return
    const leftOrderLines = orderLines.filter((_, index) => index !== row.index)

    setField("orderLines", leftOrderLines, { shouldDirty: true })
  }, [orderLines, setField, row.index])

  const onCancelPO = () => {
    // setField("status", OrderStatus.Cancelled)
    const input = createOrderUpdateInput(order, {
      status: OrderStatus.Cancelled,
    })

    updatePO({ input })
    handleBack()
  }

  return {
    onRemoveRow,
    onCancelPO,
    multipleProductsPO: (orderLines?.length || 0) > 1,
  }
}
